/**
 * The date band (§5.13): one pill per measured day (the 20 most recent, `bandDays`), the
 * selected one in violet. A day shows its presence only inside the summary's exact 14-day
 * series (`BandDay.presence !== undefined`); older days show the date alone, because
 * per-cell rates do not average into a day's rate.
 */

import { useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import { pct } from '../personaFormat';
import type { BandDay } from './conversationView';

export function DateBand({ days, selected, onSelect }: { days: BandDay[]; selected: string | null; onSelect: (day: string) => void }) {
  const { t, i18n } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const measured = days.map((d) => d.day);
  const i = selected ? measured.indexOf(selected) : -1;

  useEffect(() => {
    const el = scroller.current?.querySelector<HTMLElement>('[data-selected="true"]');
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [selected, days.length]);

  let lastMonth = '';
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.75, p: 1.25, mb: 2, minWidth: 0, maxWidth: '100%', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: '0 1px 2px rgba(23,21,29,.05), 0 4px 16px rgba(23,21,29,.05)', position: 'sticky', top: 0, zIndex: 5 }}>
      <IconButton aria-label={t('conversations.view.bandPrev')} disabled={i <= 0} onClick={() => onSelect(measured[i - 1])} size="small" sx={{ alignSelf: 'center' }}>
        <ChevronLeftIcon />
      </IconButton>
      <Box ref={scroller} sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', flex: 1, minWidth: 0, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {days.map((d) => {
          const date = new Date(`${d.day}T12:00:00Z`);
          const month = date.toLocaleDateString(i18n.language, { month: 'short', timeZone: 'UTC' });
          const monthMark = lastMonth && month !== lastMonth ? month : null;
          lastMonth = month;
          const sel = d.day === selected;
          const label = `${date.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}`;
          const title = d.presence != null ? t('conversations.view.bandDayPresence', { date: label, pct: pct(d.presence) }) : label;
          return [
            monthMark && (
              <Box key={`${d.day}-m`} aria-hidden sx={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', px: 0.25, borderLeft: '1px solid', borderColor: 'divider' }}>
                {monthMark}
              </Box>
            ),
            <Tooltip key={d.day} title={title}>
              <Box
                component="button"
                type="button"
                data-selected={sel ? 'true' : undefined}
                aria-pressed={sel}
                aria-label={title}
                onClick={() => onSelect(d.day)}
                sx={{
                  all: 'unset', flex: 'none', width: 56, py: 0.75, borderRadius: '10px', border: '1px solid', boxSizing: 'border-box',
                  borderColor: sel ? 'primary.light' : 'transparent', bgcolor: sel ? '#F3EEFF' : 'transparent',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: sel ? '#F3EEFF' : '#F5F3FA' },
                }}
              >
                <Typography component="span" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                  {date.toLocaleDateString(i18n.language, { weekday: 'short', timeZone: 'UTC' }).replace('.', '')}
                </Typography>
                <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, color: sel ? 'primary.dark' : 'text.primary' }}>{date.getUTCDate()}</Typography>
                <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', height: 14 }}>
                  {d.presence != null ? pct(d.presence) : ''}
                </Typography>
                <Box sx={{ width: 26, height: 3, borderRadius: 2, overflow: 'hidden', bgcolor: '#EEE' }}>
                  {d.presence != null && (
                    <Box sx={{ width: `${d.presence * 100}%`, height: '100%', bgcolor: sel ? 'primary.main' : 'primary.light' }} />
                  )}
                </Box>
              </Box>
            </Tooltip>,
          ];
        })}
      </Box>
      <IconButton aria-label={t('conversations.view.bandNext')} disabled={i < 0 || i >= measured.length - 1} onClick={() => onSelect(measured[i + 1])} size="small" sx={{ alignSelf: 'center' }}>
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
}
